import PubNub from 'pubnub';

// 1 minute : délai avant un nouvel essai (refresh du jeton, reconnexion), et fenêtre dans laquelle
// un second refus du jeton attend ce délai au lieu de relancer un refresh tout de suite.
const retryDelay = 60000;

// Depuis pubnub 11, l'abonnement passe par l'event engine : un jeton refusé arrive comme une erreur
// de connexion dont `error` porte la cause, une connexion rétablie comme PNConnectedCategory. Une
// erreur de connexion n'est émise qu'une fois les essais du SDK épuisés : il ne réessaie plus seul.
const connectionLostCategories = [
  'PNConnectionErrorCategory',
  'PNDisconnectedUnexpectedlyCategory',
  'PNNetworkDownCategory'
];

const isAccessDenied = status =>
  status.category === 'PNAccessDeniedCategory' || status.error === 'PNAccessDeniedCategory';

export default async function setupPubNub(
  getEventSubscriptionInfoCB,
  onMessage,
  onNetworkRecovery
) {
  let pubnub;
  let previousSubscribeKey;
  let nextRefreshToken;
  let nextReconnectToken;
  // Des événements ont pu être manqués : le rattrapage est dû.
  let needNetworkRecovery = false;
  // La connexion PubNub est tombée : le rattrapage attend qu'elle soit rétablie.
  let isConnectionLost = false;
  let needReconnect = false;
  let lastAccessDeniedRefresh = -Infinity;

  const recoverNetwork = () => {
    if (!needNetworkRecovery) return;

    needNetworkRecovery = false;
    onNetworkRecovery?.();
  };

  const scheduleRefresh = delay => {
    clearTimeout(nextRefreshToken);
    nextRefreshToken = setTimeout(refreshSubscriptionInfo, delay);
  };

  const scheduleReconnect = () => {
    clearTimeout(nextReconnectToken);
    nextReconnectToken = setTimeout(() => pubnub.reconnect(), retryDelay);
  };

  const onConnectionLost = () => {
    needNetworkRecovery = true;
    isConnectionLost = true;
  };

  // Un jeton refusé juste après avoir été renouvelé ne le sera pas mieux tout de suite : on attend.
  const onAccessDenied = () => {
    needReconnect = true;

    if (Date.now() - lastAccessDeniedRefresh < retryDelay) {
      scheduleRefresh(retryDelay);
      return;
    }

    lastAccessDeniedRefresh = Date.now();
    refreshSubscriptionInfo().catch(err =>
      console.error('Error while subscribing to PubNub :', err.message)
    );
  };

  const refreshSubscriptionInfo = async () => {
    clearTimeout(nextRefreshToken);

    let subscriptionInfo;
    let previousPubnub;

    try {
      subscriptionInfo = await getEventSubscriptionInfoCB();
    } catch (err) {
      // user anonymous: we don't subscribe
      if (err.response && err.response.status === 403) return;

      console.error('Error while subscribing to PubNub :', err.message);

      // Retry
      scheduleRefresh(retryDelay);
      needNetworkRecovery = true;

      return;
    }

    const { subscribeKey, userId, channels, ttl, token } = subscriptionInfo;
    const hasSubscribeKeyChanged = previousSubscribeKey && previousSubscribeKey !== subscribeKey;

    if (!pubnub || hasSubscribeKeyChanged) {
      previousSubscribeKey = subscribeKey;
      previousPubnub = pubnub;
      pubnub = new PubNub({ subscribeKey, userId, ssl: true, restore: true });

      pubnub.addListener({
        message: message => onMessage(message.message),
        status: status => {
          if (isAccessDenied(status)) {
            onConnectionLost();
            onAccessDenied();
          } else if (connectionLostCategories.includes(status.category)) {
            onConnectionLost();
            scheduleReconnect();
          } else if (status.category === 'PNConnectedCategory') {
            clearTimeout(nextReconnectToken);
            isConnectionLost = false;
            recoverNetwork();
          }
        }
      });
    }

    pubnub.setToken(token);

    try {
      pubnub.subscribe({ channels });
    } catch (err) {
      console.error('Error while subscribing to PubNub :', err.message);

      // Retry
      scheduleRefresh(retryDelay);
      needNetworkRecovery = true;

      return;
    }

    // S'abonner aux canaux déjà souscrits ne relance pas une connexion abandonnée sur un refus.
    if (needReconnect) {
      needReconnect = false;
      pubnub.reconnect();
    }

    if (previousPubnub != null) {
      previousPubnub.removeAllListeners();
      previousPubnub.destroy();
    }

    if (!isConnectionLost) {
      recoverNetwork();
    }

    scheduleRefresh(getRefreshDelay(ttl));
  };

  await refreshSubscriptionInfo();
}

// Min value : 1 minute
const getRefreshDelay = ttl => Math.max(1, ttl - 1) * 60000;
